import { useState, useId, forwardRef, type InputHTMLAttributes } from 'react';
import { Input } from './Input';
import { Icon } from '../Icon/Icon';
import styles from './PasswordInput.module.css';

export interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /**
   * Field label. Will be displayed as a label inside the field
   */
  label?: string;
  /**
   * Error message. When provided, the field is displayed in error state
   */
  error?: string;
  /**
   * Field size
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Field placeholder
   */
  placeholder?: string;
  /**
   * Controlled field value
   */
  value?: string;
  /**
   * Uncontrolled default value
   */
  defaultValue?: string;
  /**
   * Callback when value changes
   */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * If true, disables the field
   */
  disabled?: boolean;
  /**
   * Field ID
   */
  id?: string;
  /**
   * Field name
   */
  name?: string;
  /**
   * Additional CSS class
   */
  className?: string;
  /**
   * Callback when field receives focus
   */
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * Callback when field loses focus
   */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * ARIA label for the toggle button
   */
  toggleButtonLabel?: {
    show: string;
    hide: string;
  };
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({
    label = 'Contraseña',
    size = 'md',
    error,
    disabled,
    className,
    toggleButtonLabel = {
      show: 'Mostrar contraseña',
      hide: 'Ocultar contraseña',
    },
    ...props
  }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const generatedId = useId();
    const inputId = props.id || generatedId;

    const handleToggleVisibility = () => {
      setShowPassword((prev) => !prev);
    };

    const toggleIcon = showPassword ? (
      <Icon name="eye-off" size="md" className={styles.toggleIcon} />
    ) : (
      <Icon name="eye-on" size="md" className={styles.toggleIcon} />
    );

    return (
      <div className={[styles.passwordInputContainer, className, error ? styles.error : ''].filter(Boolean).join(' ')}>
        <Input
          ref={ref}
          id={inputId}
          type={showPassword ? 'text' : 'password'}
          label={label}
          size={size}
          hasError={!!error}
          disabled={disabled}
          helpIcon={
            <button
              type="button"
              className={styles.toggleButton}
              onClick={handleToggleVisibility}
              disabled={disabled}
              aria-label={showPassword ? toggleButtonLabel.hide : toggleButtonLabel.show}
              aria-pressed={showPassword}
            >
              {toggleIcon}
            </button>
          }
          hideHelpIconOnStates={false}
          aria-describedby={error ? `${inputId}-error` : undefined}
          {...props}
        />
        {error && (
          <div id={`${inputId}-error`} className={styles.errorMessage}>
            {error}
          </div>
        )}
      </div>
    );
  }
);

PasswordInput.displayName = 'PasswordInput';
